import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
export interface UploadedMedia {
  id: string;
  name: string;
  url: string;
  size: string;
  uploadedAt: string;
}
export interface ThemeImages {
  heroBg: string;
  featuresBg: string;
  pricingBg: string;
  loginBg: string;
  logo: string;
}
export interface ThemeConfig {
  id: string;
  name: string;
  colors: {
    primary: string;
    primaryHover: string;
    primaryGlow: string;
    accent: string;
    accentHover: string;
    bgPrimary: string;
    bgSecondary: string;
    bgSurface: string;
    bgCard: string;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    border: string;
    borderHover: string;
  };
  fonts: {
    heading: string;
    body: string;
  };
  images: ThemeImages;
  borderRadius: string;
  navStyle: 'transparent' | 'solid' | 'glass';
  cardStyle: 'flat' | 'raised' | 'glass' | 'bordered';
  buttonStyle: 'rounded' | 'pill' | 'square';
}
const defaultImages: ThemeImages = {
  heroBg: '',
  featuresBg: '',
  pricingBg: '',
  loginBg: '',
  logo: ''
};
const prebuiltThemes: ThemeConfig[] = [{
  id: 'fox-default',
  name: 'Fox Orange',
  colors: {
    primary: '#ea580c',
    primaryHover: '#f97316',
    primaryGlow: 'rgba(234,88,12,0.3)',
    accent: '#f59e0b',
    accentHover: '#fbbf24',
    bgPrimary: '#020617',
    bgSecondary: '#0f172a',
    bgSurface: '#1e293b',
    bgCard: '#0f172a',
    textPrimary: '#f1f5f9',
    textSecondary: '#cbd5e1',
    textMuted: '#64748b',
    border: '#1e293b',
    borderHover: '#334155'
  },
  fonts: {
    heading: 'Outfit',
    body: 'Inter'
  },
  images: {
    ...defaultImages
  },
  borderRadius: '1rem',
  navStyle: 'transparent',
  cardStyle: 'bordered',
  buttonStyle: 'pill'
}, {
  id: 'ocean-blue',
  name: 'Ocean Blue',
  colors: {
    primary: '#2563eb',
    primaryHover: '#3b82f6',
    primaryGlow: 'rgba(37,99,235,0.3)',
    accent: '#06b6d4',
    accentHover: '#22d3ee',
    bgPrimary: '#020617',
    bgSecondary: '#0c1222',
    bgSurface: '#162032',
    bgCard: '#0c1222',
    textPrimary: '#e2e8f0',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
    border: '#1e3a5f',
    borderHover: '#2563eb'
  },
  fonts: {
    heading: 'Outfit',
    body: 'Inter'
  },
  images: {
    ...defaultImages
  },
  borderRadius: '0.75rem',
  navStyle: 'glass',
  cardStyle: 'glass',
  buttonStyle: 'rounded'
}, {
  id: 'forest-green',
  name: 'Forest Green',
  colors: {
    primary: '#16a34a',
    primaryHover: '#22c55e',
    primaryGlow: 'rgba(22,163,74,0.3)',
    accent: '#84cc16',
    accentHover: '#a3e635',
    bgPrimary: '#021a0a',
    bgSecondary: '#0a1f10',
    bgSurface: '#132b1a',
    bgCard: '#0a1f10',
    textPrimary: '#ecfdf5',
    textSecondary: '#a7f3d0',
    textMuted: '#6ee7b7',
    border: '#14532d',
    borderHover: '#16a34a'
  },
  fonts: {
    heading: 'Outfit',
    body: 'Inter'
  },
  images: {
    ...defaultImages
  },
  borderRadius: '0.5rem',
  navStyle: 'solid',
  cardStyle: 'flat',
  buttonStyle: 'rounded'
}, {
  id: 'midnight-purple',
  name: 'Midnight Purple',
  colors: {
    primary: '#9333ea',
    primaryHover: '#a855f7',
    primaryGlow: 'rgba(147,51,234,0.3)',
    accent: '#ec4899',
    accentHover: '#f472b6',
    bgPrimary: '#0a0015',
    bgSecondary: '#120024',
    bgSurface: '#1e0038',
    bgCard: '#120024',
    textPrimary: '#f5f3ff',
    textSecondary: '#c4b5fd',
    textMuted: '#8b5cf6',
    border: '#2e1065',
    borderHover: '#7c3aed'
  },
  fonts: {
    heading: 'Outfit',
    body: 'Inter'
  },
  images: {
    ...defaultImages
  },
  borderRadius: '1.5rem',
  navStyle: 'glass',
  cardStyle: 'glass',
  buttonStyle: 'pill'
}, {
  id: 'crimson-red',
  name: 'Crimson Red',
  colors: {
    primary: '#dc2626',
    primaryHover: '#ef4444',
    primaryGlow: 'rgba(220,38,38,0.3)',
    accent: '#f97316',
    accentHover: '#fb923c',
    bgPrimary: '#0c0000',
    bgSecondary: '#1a0505',
    bgSurface: '#2a0a0a',
    bgCard: '#1a0505',
    textPrimary: '#fef2f2',
    textSecondary: '#fca5a5',
    textMuted: '#f87171',
    border: '#450a0a',
    borderHover: '#dc2626'
  },
  fonts: {
    heading: 'Outfit',
    body: 'Inter'
  },
  images: {
    ...defaultImages
  },
  borderRadius: '0.5rem',
  navStyle: 'solid',
  cardStyle: 'raised',
  buttonStyle: 'square'
}, {
  id: 'arctic-frost',
  name: 'Arctic Frost',
  colors: {
    primary: '#0ea5e9',
    primaryHover: '#38bdf8',
    primaryGlow: 'rgba(14,165,233,0.3)',
    accent: '#e2e8f0',
    accentHover: '#f1f5f9',
    bgPrimary: '#f8fafc',
    bgSecondary: '#f1f5f9',
    bgSurface: '#e2e8f0',
    bgCard: '#ffffff',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    border: '#cbd5e1',
    borderHover: '#0ea5e9'
  },
  fonts: {
    heading: 'Outfit',
    body: 'Inter'
  },
  images: {
    ...defaultImages
  },
  borderRadius: '1rem',
  navStyle: 'glass',
  cardStyle: 'raised',
  buttonStyle: 'rounded'
}];
interface ThemeContextType {
  currentTheme: ThemeConfig;
  setTheme: (theme: ThemeConfig) => void;
  prebuiltThemes: ThemeConfig[];
  resetTheme: () => void;
  uploadedMedia: UploadedMedia[];
  addMedia: (media: UploadedMedia) => void;
  removeMedia: (id: string) => void;
  saveTheme: (theme: ThemeConfig) => Promise<void>;
  savedCustomThemes: ThemeConfig[];
  saveCustomTheme: (name: string) => Promise<void>;
  deleteCustomTheme: (id: string) => Promise<void>;
  hasUnsavedChanges: boolean;
  lastSavedTheme: ThemeConfig;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
function applyThemeToDOM(theme: ThemeConfig) {
  const root = document.documentElement;
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--theme-${key}`, value);
  });
  root.style.setProperty('--theme-font-heading', theme.fonts.heading);
  root.style.setProperty('--theme-font-body', theme.fonts.body);
  root.style.setProperty('--theme-radius', theme.borderRadius);
  root.style.setProperty('--theme-nav-style', theme.navStyle);
  root.style.setProperty('--theme-card-style', theme.cardStyle);
  root.style.setProperty('--theme-button-style', theme.buttonStyle);
  // Image CSS vars
  root.style.setProperty('--theme-hero-bg', theme.images.heroBg ? `url(${theme.images.heroBg})` : 'none');
  root.style.setProperty('--theme-features-bg', theme.images.featuresBg ? `url(${theme.images.featuresBg})` : 'none');
  root.style.setProperty('--theme-pricing-bg', theme.images.pricingBg ? `url(${theme.images.pricingBg})` : 'none');
  root.style.setProperty('--theme-login-bg', theme.images.loginBg ? `url(${theme.images.loginBg})` : 'none');
}
export function ThemeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig>(prebuiltThemes[0]);
  const [lastSavedTheme, setLastSavedTheme] = useState<ThemeConfig>(prebuiltThemes[0]);
  const [savedCustomThemes, setSavedCustomThemes] = useState<ThemeConfig[]>([]);
  const [uploadedMedia, setUploadedMedia] = useState<UploadedMedia[]>([]);
  useEffect(() => {
    async function loadSettings() {
      try {
        const {
          data,
          error
        } = await supabase.from('site_settings').select('key, value').in('key', ['active_theme', 'custom_themes']);
        if (error) throw error;
        if (data) {
          const activeThemeRow = data.find(r => r.key === 'active_theme');
          if (activeThemeRow && activeThemeRow.value) {
            setCurrentTheme(activeThemeRow.value as ThemeConfig);
            setLastSavedTheme(activeThemeRow.value as ThemeConfig);
          }
          const customThemesRow = data.find(r => r.key === 'custom_themes');
          if (customThemesRow && customThemesRow.value) {
            setSavedCustomThemes(customThemesRow.value as ThemeConfig[]);
          }
        }
      } catch (err) {
        console.error('Failed to load theme settings:', err);
      }
    }
    loadSettings();
  }, []);
  useEffect(() => {
    applyThemeToDOM(currentTheme);
  }, [currentTheme]);
  const setTheme = (theme: ThemeConfig) => setCurrentTheme(theme);
  const resetTheme = () => setCurrentTheme(prebuiltThemes[0]);
  const addMedia = (media: UploadedMedia) => setUploadedMedia(prev => [...prev, media]);
  const removeMedia = (id: string) => setUploadedMedia(prev => prev.filter(m => m.id !== id));
  const saveTheme = async (theme: ThemeConfig) => {
    try {
      const {
        error
      } = await supabase.from('site_settings').upsert({
        key: 'active_theme',
        value: theme,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'key'
      });
      if (error) throw error;
      setLastSavedTheme(theme);
    } catch (err) {
      console.error('Failed to save theme:', err);
      throw err;
    }
  };
  const saveCustomTheme = async (name: string) => {
    try {
      const newTheme: ThemeConfig = {
        ...currentTheme,
        id: `custom-${Date.now()}`,
        name
      };
      const updatedThemes = [...savedCustomThemes, newTheme];
      const {
        error
      } = await supabase.from('site_settings').upsert({
        key: 'custom_themes',
        value: updatedThemes,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'key'
      });
      if (error) throw error;
      setSavedCustomThemes(updatedThemes);
    } catch (err) {
      console.error('Failed to save custom theme:', err);
      throw err;
    }
  };
  const deleteCustomTheme = async (id: string) => {
    try {
      const updatedThemes = savedCustomThemes.filter(t => t.id !== id);
      const {
        error
      } = await supabase.from('site_settings').upsert({
        key: 'custom_themes',
        value: updatedThemes,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'key'
      });
      if (error) throw error;
      setSavedCustomThemes(updatedThemes);
    } catch (err) {
      console.error('Failed to delete custom theme:', err);
      throw err;
    }
  };
  const hasUnsavedChanges = JSON.stringify(currentTheme) !== JSON.stringify(lastSavedTheme);
  return <ThemeContext.Provider value={{
    currentTheme,
    setTheme,
    prebuiltThemes,
    resetTheme,
    uploadedMedia,
    addMedia,
    removeMedia,
    saveTheme,
    savedCustomThemes,
    saveCustomTheme,
    deleteCustomTheme,
    hasUnsavedChanges,
    lastSavedTheme
  }} data-id="element-1995">
      {children}
    </ThemeContext.Provider>;
}
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}